import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getVehicleTypes } from '../../defa-find-engine-heater/utils/vehicle-types';
import { getVehicleMakesAsync, IVehicleMakes } from '../../defa-find-engine-heater/actions/get-vehicle-makes';
import { getVehicleModelsAsync, IVehicleModel } from '../../defa-find-engine-heater/actions/get-vehicle-models';
import { IVehicleType } from '../../defa-find-engine-heater/defa-find-engine-heater';
import { ISearchResultProduct, searchVehiclesAsync } from '../../defa-find-engine-heater/actions/search-vehicles';
import { IDefaSearchResultContainerResources } from '../defa-search-result-container.props.autogenerated';

type CountValuePair = { Count: number; Value: string; };

export interface IFindEngineHeaderSearchParams {
    searchType: 'registration' | 'manual';
    type: string;
    typeId: number;
    make: string;
    makeId: number;
    model: string;
    modelId: number | 'all';
    modelSpec?: string;
    engine?: string;
    year?: string;
    registrationNumber?: string;
}

export interface IFindEngineHeaderProps {
    context: ICoreContext;
    resources: IDefaSearchResultContainerResources;
    initialValues?: IFindEngineHeaderSearchParams;
    resetSignal?: boolean;
    onClose?: () => void;
    onSearch?: (searchParams: IFindEngineHeaderSearchParams | undefined, results: ISearchResultProduct[]) => void;
}

function arrayUniqueFilterSort(input: (string | undefined)[]): string[] {
    return ArrayExtensions.unique(input).filter(Boolean).sort() as string[];
}

function autoSelectIfSingle(values: CountValuePair[], setter: (value: string | undefined) => void, enabled: boolean): () => void {
    return () => {
        if (!enabled) {
            setter(undefined);
            return;
        }
        const valuesWithCount = values.filter(x => x.Count > 0);
        if (valuesWithCount.length === 1) {
            setter(valuesWithCount[0].Value);
        }
    };
}

const useGetMakes = (context: ICoreContext, type: IVehicleType | undefined) => {
    const [makes, setMakes] = React.useState<IVehicleMakes[]>([]);

    React.useEffect(() => {
        if (!type?.Id) {
            setMakes([]);
            return;
        }

        const fetchMakes = async () => {
            const makesResponse = await getVehicleMakesAsync({ callerContext: context.actionContext }, Number(type.Id));
            setMakes(makesResponse.Makes || []);
        };
        void fetchMakes();
    }, [context, type?.Id]);

    return makes;
};

const useGetModels = (context: ICoreContext, type: IVehicleType | undefined, make: IVehicleMakes | undefined) => {
    const [models, setModels] = React.useState<IVehicleModel[]>([]);

    React.useEffect(() => {
        if (!type?.Id || !make?.Id) {
            setModels([]);
            return;
        }
        const fetchModels = async () => {
            const modelsResponse = await getVehicleModelsAsync({ callerContext: context.actionContext }, Number(type.Id), make.Id);
            modelsResponse.Models?.unshift({ Id: 'all', Name: 'All' });
            setModels(modelsResponse.Models || []);
        };
        void fetchModels();
    }, [context, type?.Id, make?.Id]);

    return models;
};

const useGetSearchResults = (
    context: ICoreContext,
    type: IVehicleType | undefined,
    make: IVehicleMakes | undefined,
    model: IVehicleModel | undefined
) => {
    const [searchResults, setSearchResults] = React.useState<ISearchResultProduct[]>([]);

    React.useEffect(() => {
        if (!type?.Id || !make?.Id || !model?.Id) {
            setSearchResults([]);
            return;
        }

        const fetchSearchResults = async () => {
            const result = await searchVehiclesAsync({ callerContext: context.actionContext }, Number(type.Id), make.Id, model.Id);
            setSearchResults(result.Vehicles || []);
        };
        void fetchSearchResults();
    }, [context, type?.Id, make?.Id, model?.Id]);

    const resetSearchResults = () => {
        setSearchResults([]);
    };

    return [searchResults, resetSearchResults] as const;
};

export const FindEngineHeater: React.FC<IFindEngineHeaderProps> = ({
    context,
    resources,
    initialValues,
    resetSignal,
    onClose,
    onSearch,
    ...props
}) => {
    const types = getVehicleTypes(context.request);

    const [selectedType, setSelectedType] = React.useState<IVehicleType | undefined>(undefined);
    const [selectedMake, setSelectedMake] = React.useState<IVehicleMakes | undefined>(undefined);
    const [selectedModel, setSelectedModel] = React.useState<IVehicleModel | undefined>(undefined);
    const [selectedSpec, setSelectedSpec] = React.useState<string | undefined>(undefined);
    const [selectedEngine, setSelectedEngine] = React.useState<string | undefined>(undefined);
    const [selectedYear, setSelectedYear] = React.useState<string | undefined>(undefined);

    const makes = useGetMakes(context, selectedType);
    const models = useGetModels(context, selectedType, selectedMake);
    const [searchResults, resetSearchResults] = useGetSearchResults(context, selectedType, selectedMake, selectedModel);

    React.useEffect(() => {
        if (initialValues?.type && initialValues.typeId) {
            setSelectedType({ Id: String(initialValues.typeId), Name: initialValues.type });
        }
        if (initialValues?.make && initialValues.makeId) {
            setSelectedMake({ Id: Number(initialValues.makeId), Name: initialValues.make });
        }
        if (initialValues?.model && initialValues.modelId) {
            const name = initialValues.modelId === 'all' ? 'All' : initialValues.model;
            setSelectedModel({ Id: initialValues.modelId === 'all' ? 'all' : Number(initialValues.modelId), Name: name });
        }
        setSelectedEngine(initialValues?.engine);
        setSelectedSpec(initialValues?.modelSpec);
        setSelectedYear(initialValues?.year);
    }, [initialValues]);

    const countMatches = (spec?: string, engine?: string, year?: string): number => {
        const filteredResults = searchResults
            .filter(r => (spec ? (r.specs || 'unknown') === spec : true))
            .filter(r => (engine ? (r.ehengcode || 'unknown') === engine : true))
            .filter(r => (year ? (r.modyear || 'unknown') === year : true));
        const uniqueProductIds = new Set(filteredResults.flatMap(r => [ r.ehengheater, r.ehmountkit ]));
        return Array.from(uniqueProductIds).filter(Boolean).length;
    };
    const getCountValuePairs = (property: keyof ISearchResultProduct, spec?: string | true, engine?: string | true, year?: string | true): CountValuePair[] => {
        return (searchResults.some(x => !x[property]) ? ['unknown'] : [])
            .concat(arrayUniqueFilterSort(searchResults.map(x => x[property])))
            .map(x => ({
                Count: countMatches(
                    spec === true ? x : spec,
                    engine === true ? x : engine,
                    year === true ? x : year
                ),
                Value: x
            }));
    };

    const specs = React.useMemo(() => getCountValuePairs('specs', true), [searchResults]);
    const engines = React.useMemo(() => getCountValuePairs('ehengcode', selectedSpec, true), [searchResults, selectedSpec]);
    const years = React.useMemo(() => getCountValuePairs('modyear', selectedSpec, selectedEngine, true), [searchResults, selectedSpec, selectedEngine]);

    React.useEffect(() => {
        if (resetSignal === undefined) {
            return;
        }
        setSelectedType(undefined);
        setSelectedMake(undefined);
        setSelectedModel(undefined);
        setSelectedSpec(undefined);
        setSelectedEngine(undefined);
        setSelectedYear(undefined);
        resetSearchResults();
    }, [!!resetSignal]);

    // Auto select model if only one option is available (after 'All' option)
    React.useEffect(() => {
        if (models.length === 2) {
            setSelectedModel(models[1]);
        }
    }, [models]);

    // Auto select spec, engine, year if only one option is available
    React.useEffect(autoSelectIfSingle(specs, setSelectedSpec, true), [specs]);
    React.useEffect(autoSelectIfSingle(engines, setSelectedEngine, !!selectedSpec), [engines]);
    React.useEffect(autoSelectIfSingle(years, setSelectedYear, !!selectedEngine), [years]);

    const doSearch = () => {
        const filteredResults = searchResults
            .filter(r => (selectedSpec ? (selectedSpec === 'unknown' ? !r.specs : r.specs === selectedSpec) : true))
            .filter(r => (selectedEngine ? (selectedEngine === 'unknown' ? !r.ehengcode : r.ehengcode === selectedEngine) : true))
            .filter(r => (selectedYear ? (selectedYear === 'unknown' ? !r.modyear : r.modyear === selectedYear) : true));
        const params: IFindEngineHeaderSearchParams = {
            searchType: 'manual',
            type: selectedType?.Name || '',
            typeId: selectedType?.Id ? Number(selectedType.Id) : 0,
            make: selectedMake?.Name || '',
            makeId: selectedMake?.Id || 0,
            model: selectedModel?.Name || '',
            modelId: selectedModel?.Id ? Number(selectedModel.Id) : 0,
            modelSpec: selectedSpec,
            engine: selectedEngine,
            year: selectedYear,
            registrationNumber: ''
        };
        onSearch?.(params, filteredResults);
    };

    const setSelected = (type?: IVehicleType, make?: IVehicleMakes, model?: IVehicleModel, spec?: string, engine?: string, year?: string) => {
        setSelectedType(type);
        setSelectedMake(make);
        setSelectedModel(model);
        setSelectedSpec(spec);
        setSelectedEngine(engine);
        setSelectedYear(year);
    };

    const canSearch = !!(selectedType?.Id && selectedMake?.Id && selectedModel?.Id && selectedSpec && selectedEngine && selectedYear);

    return (
        <div className='ms-search-result-container__find-engine-heater'>
            <label>
                {resources.findEngineHeaterVehicleTypeLabel}:
                <select
                    value={selectedType?.Id || ''}
                    onChange={e => {
                        const type = e.target.value && types?.find(type => type.Id === e.target.value);
                        setSelected(type || selectedType);
                        resetSearchResults();
                    }}
                >
                    <option selected disabled value=''>
                        {resources.findEngineHeaterSelectOptionText}
                    </option>
                    {types?.map((type) => (
                        <option key={type.Id} value={type.Id}>
                            {type.Name}
                        </option>
                    ))}
                </select>
            </label>

            <label>
                {resources.findEngineHeaterMakeLabel}:
                <select disabled={!selectedType?.Id || makes.length === 0} value={selectedMake?.Id || ''} onChange={e => {
                    const make = e.target.value && makes.find(m => m.Id === Number(e.target.value));
                    setSelected(selectedType, make || selectedMake);
                    resetSearchResults();
                }}>
                    <option selected disabled value=''>
                        {resources.findEngineHeaterSelectOptionText}
                    </option>
                    {makes.map((make) => (
                        <option key={make.Id} value={make.Id}>
                            {make.Name}
                        </option>
                    ))}
                </select>
            </label>

            <label>
                {resources.findEngineHeaterModelLabel}:
                <select disabled={!selectedMake?.Id} value={selectedModel?.Id || ''} onChange={e => {
                    const model = e.target.value && models.find(m => m.Id === e.target.value || m.Id === Number(e.target.value));
                    setSelected(selectedType, selectedMake, model || selectedModel);
                    resetSearchResults();
                }}>
                    <option selected disabled value=''>
                        {resources.findEngineHeaterSelectOptionText}
                    </option>
                    {models.map((model) => {
                        const count = selectedModel === model ? countMatches() : 0;
                        return (
                            <option key={model.Id} value={model.Id}>
                                {`${model.Name}${count ? ` (${count})` : ''}`}
                            </option>
                        );
                    })}
                </select>
            </label>

            <label>
                {resources.findEngineHeaterModelSpecLabel}:
                <select disabled={!selectedModel?.Id} value={selectedSpec || ''} onChange={e => {
                    setSelected(selectedType, selectedMake, selectedModel, e.target.value || undefined);
                }}>
                    <option selected value=''>
                        {resources.findEngineHeaterSelectOptionText}
                    </option>
                    {specs.filter(x => x.Count > 0).map((spec) => {
                        return (
                            <option key={spec.Value} value={spec.Value}>
                                {`${spec.Value === 'unknown' ? resources.findEngineHeaterUnknownOptionText : spec.Value} (${spec.Count})`}
                            </option>
                        );
                    })}
                </select>
            </label>

            <label>
                {resources.findEngineHeaterEngineLabel}:
                <select disabled={!selectedSpec} value={selectedEngine || ''} onChange={e => {
                    setSelected(selectedType, selectedMake, selectedModel, selectedSpec, e.target.value || undefined);
                }}>
                    <option selected value=''>
                        {resources.findEngineHeaterSelectOptionText}
                    </option>
                    {engines.filter(x => x.Count > 0).map((engine) => {
                        return (
                            <option key={engine.Value} value={engine.Value}>
                                {`${engine.Value === 'unknown' ? resources.findEngineHeaterUnknownOptionText : engine.Value} (${engine.Count})`}
                            </option>
                        );
                    })}
                </select>
            </label>

            <label>
                {resources.findEngineHeaterYearLabel}:
                <select disabled={!selectedEngine} value={selectedYear || ''} onChange={e => {
                    setSelected(selectedType, selectedMake, selectedModel, selectedSpec, selectedEngine, e.target.value || undefined);
                }}>
                    <option selected value=''>
                        {resources.findEngineHeaterSelectOptionText}
                    </option>
                    {years.filter(x => x.Count > 0).map((year) => {
                        return (
                            <option key={year.Value} value={year.Value}>
                                {`${year.Value === 'unknown' ? resources.findEngineHeaterUnknownOptionText : year.Value} (${year.Count})`}
                            </option>
                        );
                    })}
                </select>
            </label>

            <div style={{ display: 'flex' }}>
                <button
                    className='ms-search-result-container__find-engine-heater__search-button'
                    disabled={searchResults.length === 0 || !canSearch}
                    onClick={doSearch}
                >
                    {resources.searchText}
                </button>
                <button
                    className='ms-search-result-container__find-engine-heater__close-button'
                    onClick={() => onClose?.()}
                >
                    {resources.cancelBtnLabel}
                </button>
                <button
                    className='ms-search-result-container__find-engine-heater__reset-button'
                    disabled={
                        !selectedType?.Id &&
                        !selectedMake?.Id &&
                        !selectedModel?.Id &&
                        !selectedSpec &&
                        !selectedEngine &&
                        !selectedYear
                    }
                    onClick={() => setSelected()}
                >
                    {resources.resetBtnLabel}
                </button>
            </div>
        </div>
    );
};
